import { createAction, handleActions, type Action } from 'redux-actions';
import type { Dispatch } from 'react';
import type { CoverageState, PrimeXQuoteState, RatingOptions } from '../types';
import type {
  GlobalConfig,
  RateModifier,
  RaterXCoverage,
  RaterXPreset,
} from '../api/types/rater-x-preset';
import type { CustomizeQuoteState } from '../views/customize-quote/types';
import type { GetQuoteCartListResponse } from '../../../../../api/quotes.api.types';

const INITIAL_STATE = {};

/* ACTION TYPES */
const EFFECTIVE_DATE_ENTERED = 'EFFECTIVE_DATE_ENTERED';
const END_DATE_ENTERED = 'END_DATE_ENTERED';
const EFFECTIVE_DATE_SYNCED = 'EFFECTIVE_DATE_SYNCED';
const END_DATE_SYNCED = 'END_DATE_SYNCED';
const CART_COUNT_UPDATED = 'CART_COUNT_UPDATED';
const CART_LIST_UPDATED = 'CART_LIST_UPDATED';
const GLOBAL_INPUT_UPDATED = 'GLOBAL_INPUT_UPDATED';
const COVERAGE_UPDATED = 'COVERAGE_UPDATED';
const RATER_X_RECALCULATED = 'RATER_X_RECALCULATED';
const COMMISSION_RATE_UPDATED = 'COMMISSION_RATE_UPDATED';
const IS_RENEWAL_TOGGLED = 'IS_RENEWAL_TOGGLED';
const RATE_MODIFIER_UPDATED = 'RATE_MODIFIER_UPDATED';
const COMPONENT_GLOBAL_UPDATED = 'COMPONENT_GLOBAL_UPDATED';
const REPAIR_RATER_REQUESTED = 'REPAIR_RATER_REQUESTED';

export const createActionHandlers = (
  dispatch: Dispatch<Action<PrimeXQuoteState>>
) => {
  return {
    setEffectiveDate: (date: string) =>
      dispatch(createAction(EFFECTIVE_DATE_ENTERED)(date)),

    setEndDate: (date: string) =>
      dispatch(createAction(END_DATE_ENTERED)(date)),

    syncEffectiveDate: (date: string) =>
      dispatch(createAction(EFFECTIVE_DATE_SYNCED)(date)),

    syncEndDate: (date: string) =>
      dispatch(createAction(END_DATE_SYNCED)(date)),

    setCartCount: (count: number) =>
      dispatch(createAction(CART_COUNT_UPDATED)(count)),

    setCartList: (payload: GetQuoteCartListResponse) =>
      dispatch(createAction(CART_LIST_UPDATED)(payload)),

    setGlobalValue: (payload: GlobalInputPayload) =>
      dispatch(createAction(GLOBAL_INPUT_UPDATED)(payload)),

    setCoverage: (payload: UpdateCoveragePayload) =>
      dispatch(createAction(COVERAGE_UPDATED)(payload)),

    setRaterXRecalculated: (payload: RaterXRecalculatedPayload) =>
      dispatch(createAction(RATER_X_RECALCULATED)(payload)),

    setCommissionRate: (payload: number) =>
      dispatch(createAction(COMMISSION_RATE_UPDATED)(payload)),

    setIsRenewal: () => dispatch(createAction(IS_RENEWAL_TOGGLED)()),

    setRatingOption: (payload: RateModifierPayload) =>
      dispatch(createAction(RATE_MODIFIER_UPDATED)(payload)),

    setComponentGlobal: (key: string) => (payload: GlobalInputPayload) =>
      dispatch(
        createAction(COMPONENT_GLOBAL_UPDATED)({
          key,
          ...payload,
        })
      ),

    onRepairRaterRequested: () =>
      dispatch(createAction(REPAIR_RATER_REQUESTED)()),
  };
};

export const primeXQuotingReducer = handleActions<QuoteState, any>(
  {
    [EFFECTIVE_DATE_ENTERED]: (state, action: Action<string>) => ({
      ...state,
      effectiveDate: action.payload,
      enableRaterXRecalculate: true,
      globalChanged: true,
    }),
    [END_DATE_ENTERED]: (state, action: Action<string>) => ({
      ...state,
      endDate: action.payload,
      enableRaterXRecalculate: true,
    }),
    [EFFECTIVE_DATE_SYNCED]: (state, action: Action<string>) => ({
      ...state,
      effectiveDate: action.payload,
    }),
    [END_DATE_SYNCED]: (state, action: Action<string>) => ({
      ...state,
      endDate: action.payload,
    }),
    [CART_COUNT_UPDATED]: (state, action: Action<number>) => ({
      ...state,
      cartCount: action.payload,
    }),
    [CART_LIST_UPDATED]: (state, action: Action<GetQuoteCartListResponse>) => ({
      ...state,
      cart: action.payload,
    }),
    [GLOBAL_INPUT_UPDATED]: (state, action: Action<GlobalInputPayload>) => {
      return {
        ...state,
        enableRaterXRecalculate: true,
        globalChanged: true,
        global: {
          ...state.global,
          value: {
            ...state.global.value,
            props: {
              ...state.global.value.props,
              ...action.payload,
            },
          },
        },
      };
    },

    [COMPONENT_GLOBAL_UPDATED]: (
      state,
      action: Action<GlobalInputPayload & { key: string }>
    ) => {
      const { key, ...payload } = action.payload;
      const valToLock = Object.keys(payload);

      return {
        ...state,
        enableRaterXRecalculate: true,
        globalChanged: true,
        componentGlobals: {
          ...state.componentGlobals,
          [key]: {
            ...(state.componentGlobals?.[key] as GlobalConfig),
            lockValues: [
              ...(state.componentGlobals?.[key]?.lockValues ?? []),
              ...valToLock,
            ],
            value: {
              props: {
                ...(state.componentGlobals?.[key]?.value?.props || {}),
                ...payload,
              },
            },
          },
        },
      };
    },

    [COVERAGE_UPDATED]: (state, action: Action<UpdateCoveragePayload>) => {
      return {
        ...state,
        enableRaterXRecalculate: true,
        coverageState: {
          ...state.coverageState,
          [action.payload.id]: {
            ...state.coverageState[action.payload.id],
            ...action.payload,
            isPegValue: action.payload.isPegValue ?? true,
            isDirty: true,
            ...(action.payload.value && {
              value: {
                props: {
                  ...state.coverageState[action.payload.id]?.value?.props,
                  ...action.payload.value.props,
                },
              },
            }),
          },
        },
      };
    },
    [RATE_MODIFIER_UPDATED]: (state, action: Action<RateModifierPayload>) => {
      return {
        ...state,
        enableRaterXRecalculate: true,
        ratingOptions: {
          ...state.ratingOptions,
          [action.payload.id]: {
            isDirty: true,
            isPegValue: true,
            ...state.ratingOptions[action.payload.id],
            ...action.payload,
          },
        },
      };
    },
    [RATER_X_RECALCULATED]: (
      state,
      action: Action<RaterXRecalculatedPayload>
    ) => {
      const coverageState = Object.values(state.coverageState).reduce(
        (acc: any, coverage: any) => {
          return {
            ...acc,
            [coverage.key]: {
              ...acc[coverage.key],
              isPegValue:
                action.payload.coverages[coverage.key]?.isPegValue ?? false,
            },
          };
        },
        action.payload.coverages
      );

      const componentGlobals = Object.entries(
        state.componentGlobals || {}
      ).reduce((acc, [key, value]) => {
        return {
          ...acc,
          [key]: {
            ...value,
            ...action.payload.componentGlobals?.[key],
          },
        };
      }, {});

      return {
        ...state,
        coverageState,
        global: action.payload.global,
        ratingOptions: action.payload.ratingOptions,
        ...(action.payload.carrierPrefix && {
          carrierPrefix: action.payload.carrierPrefix,
        }),
        ...(action.payload.treatyId && {
          treatyId: action.payload.treatyId,
        }),
        componentGlobals,
        enableRaterXRecalculate: false,
        globalChanged: false,
        ...(action.payload.endDate && {
          endDate: action.payload.endDate,
        }),
        ...(action.payload.effectiveDate && {
          effectiveDate: action.payload.effectiveDate,
        }),
      };
    },

    [COMMISSION_RATE_UPDATED]: (state, action: Action<number>) => ({
      ...state,
      quoteCommissionRate: action.payload,
      enableRaterXRecalculate: true,
    }),

    [IS_RENEWAL_TOGGLED]: (state) => ({
      ...state,
      enableRaterXRecalculate: true,
      isRenewal: !state.isRenewal,
    }),

    [REPAIR_RATER_REQUESTED]: (state) => {
      return {
        ...state,
        enableRaterXRecalculate: true,
        globalChanged: true,
      };
    },
  },
  INITIAL_STATE as QuoteState
);

type GlobalInputPayload = Partial<RaterXPreset['global']['value']['props']>;

export type UpdateCoveragePayload = Partial<RaterXCoverage> & { id: string };

type RaterXRecalculatedPayload = {
  coverages: CoverageState;
  global: RaterXPreset['global'];
  ratingOptions: RatingOptions;
  componentGlobals: Record<string, GlobalConfig>;
  endDate?: string;
  effectiveDate?: string;
  carrierPrefix?: string;
  treatyId?: string;
};

type QuoteState = PrimeXQuoteState & Partial<CustomizeQuoteState>;

type RateModifierPayload = Partial<RateModifier> & { id: string };
